import { ArrowPathIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { type FC, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";

import type {
  EvalMultiModelTestDetail,
  MultiModelTestCell,
  MultiModelTestModelRollup,
  MultiModelTestReviewItem,
} from "@api/rest/types-and-hooks";
import { getEvalMultiModelTest, getGetEvalMultiModelTestQueryKey } from "@api/rest/types-and-hooks";
import { ConfettiBurst } from "@components/ModelTesting/ConfettiBurst";
import { PairwiseVerdictChip } from "@components/ModelTesting/PairwiseVerdict";
import {
  PairwiseStatus,
  SCORE_TONES,
  type ScoreCounts,
  Winner,
  modelLabel,
  scoreTone,
  sumScoreCounts,
} from "@components/ModelTesting/constants";

/** Verdict tally for one candidate: picks FOR the candidate raise the
 *  score, picks for the current model lower it, ties are neutral. */
const reviewScore = (rollup: MultiModelTestModelRollup) => {
  let wins = 0;
  let losses = 0;
  let ties = 0;
  for (const item of rollup.review) {
    if (item.winner === Winner.Candidate) wins += 1;
    else if (item.winner === Winner.Baseline) losses += 1;
    else if (item.winner === Winner.Tie) ties += 1;
  }
  return { wins, losses, ties, net: wins - losses, reviewed: wins + losses + ties };
};

/** The reviewer's saved pick, toned by what it means for the candidate:
 *  green = candidate won, red = the current model was better (a
 *  regression), neutral = tie. */
const WinnerChip: FC<{ winner: string }> = ({ winner }) => {
  if (winner === Winner.Candidate) {
    return (
      <span className="shrink-0 whitespace-nowrap rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
        candidate
      </span>
    );
  }
  if (winner === Winner.Baseline) {
    return (
      <span className="shrink-0 whitespace-nowrap rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
        current
      </span>
    );
  }
  return (
    <span className="shrink-0 whitespace-nowrap rounded bg-bg-default px-1.5 py-0.5 text-xs font-medium text-text-secondary">
      tie
    </span>
  );
};

const pct = (value: number | null | undefined): string => (value == null ? "—" : `${Math.round(value * 100)}%`);

const cellInFlight = (cell: MultiModelTestCell): boolean =>
  cell.progress.pending > 0 || cell.progress.running > 0 || cell.progress.judging > 0;

const MEASURE = "w-full";

/** One candidate's rollup card: the ranking VERDICT only — the review
 *  work-list lives in its own full-width section below. Totals stay
 *  "scoring" until every cell for this model is done. */
const ModelCard: FC<{
  rollup: MultiModelTestModelRollup;
  settled: boolean;
  done: number;
  total: number;
  winner: boolean;
  selected: boolean;
  onSelect: () => void;
  /** Judge tallies across this model's cells — tones the pass rate. */
  counts: ScoreCounts;
  /** Every model fully reviewed and this one ranked first (non-poor). */
  recommended: boolean;
}> = ({ rollup, settled, done, total, winner, selected, onSelect, counts, recommended }) => {
  const score = reviewScore(rollup);
  return (
    // The whole card selects the model's work-list — the Review button is
    // just the visible affordance, not the only target.
    <div
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect();
      }}
      tabIndex={0}
      role="button"
      aria-label={`Show review work-list for ${rollup.candidate_model}`}
      aria-pressed={selected}
      className={`relative flex cursor-pointer items-center justify-between gap-4 rounded-lg border p-4 transition-shadow ${
        recommended
          ? "border-amethyst-400 ring-2 ring-amethyst-400"
          : rollup.poor
            ? "border-red-300"
            : winner
              ? "border-green-300"
              : "border-border-subtle"
      } ${
        // Reverse emphasis: the selected card pops (ring + shadow), the
        // others recede slightly until hovered. 55% washed the whole page
        // out — 80% keeps their content legible.
        selected
          ? `bg-bg-surface shadow-md ${recommended ? "" : "ring-2 ring-limestone-900"}`
          : "bg-bg-surface opacity-80 hover:opacity-100 hover:shadow-sm"
      } focus:outline-none focus-visible:ring-2 focus-visible:ring-limestone-900`}
    >
      {recommended ? (
        // Legend-style label sitting ON the border, like a fieldset title.
        <span className="absolute -top-3.5 left-4 flex items-center gap-1.5 rounded-full border border-amethyst-400 bg-amethyst-100 px-3 py-1 text-sm font-semibold text-amethyst-700">
          <SparklesIcon className="size-5 text-amber-500" aria-hidden="true" />
          Recommended model
        </span>
      ) : null}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-text-primary" title={rollup.candidate_model}>
            {modelLabel(rollup.candidate_model)}
          </span>
          {winner && !rollup.poor && !recommended ? (
            <span className="whitespace-nowrap rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700">
              Highest
            </span>
          ) : null}
          {rollup.poor ? (
            <span
              className="whitespace-nowrap rounded bg-red-600 px-1.5 py-0.5 text-xs font-semibold text-white"
              title="At least one output was disqualified by a judge check (e.g. AI tone at the rubric's bottom anchor)."
            >
              Poor — disqualifying output
            </span>
          ) : null}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {score.reviewed > 0 ? (
            // Pass rate with the reviewer's calls applied (candidate/tie
            // → 1, current → 0); the judge's pass rate carries the tone.
            <span
              className="whitespace-nowrap rounded-md border border-border-medium bg-bg-surface px-2.5 py-1 font-semibold text-text-primary"
              title={`Pass rate with your verdicts applied: ${score.wins} candidate win${score.wins === 1 ? "" : "s"} · ${score.losses} for current · ${score.ties} tie${score.ties === 1 ? "" : "s"}`}
            >
              Total score {Math.round((rollup.adjusted_average ?? 0) * 100)}
            </span>
          ) : null}
          {settled ? (
            <span
              className={`rounded-md px-2.5 py-1 font-semibold ${scoreTone(counts)}`}
              title="Share of judged prompts the judge called same as or better than the current model"
            >
              {pct(rollup.average)} pass rate
            </span>
          ) : (
            <span className="animate-pulse rounded-md bg-bg-default px-2.5 py-1 text-xs font-medium text-text-tertiary">
              Scoring… {done}/{total} templates done
            </span>
          )}
          {rollup.unresolved_disqualified > 0 ? (
            <span
              className="whitespace-nowrap rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600"
              title={`${rollup.unresolved_disqualified} disqualified output${rollup.unresolved_disqualified === 1 ? "" : "s"} the review hasn't overruled`}
            >
              {rollup.unresolved_disqualified}⚑ DQ
            </span>
          ) : null}
        </div>
      </div>
      {/* No button once done — the card itself still selects the list. */}
      {rollup.flagged > 0 && rollup.reviewed < rollup.flagged ? (
        <div className="shrink-0 self-center">
          <Button
            text={
              rollup.reviewed > 0
                ? `Reviewing ${rollup.reviewed}/${rollup.flagged} ↓`
                : `Review ${rollup.flagged} prompts ↓`
            }
            appearance={selected ? "primary" : "secondary"}
            className="rounded-sm"
            onClick={onSelect}
          />
        </div>
      ) : null}
    </div>
  );
};

const ReasonBadge: FC<{ item: MultiModelTestReviewItem }> = ({ item }) => {
  if (item.disqualified) {
    return (
      <span className="whitespace-nowrap rounded bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">
        Disqualified
      </span>
    );
  }
  if (item.pairwise_verdict) {
    return <PairwiseVerdictChip verdict={item.pairwise_verdict} summary={item.pairwise_summary} judged />;
  }
  return item.pairwise_status === PairwiseStatus.Failed ? (
    <span className={`whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${SCORE_TONES.review}`}>
      Judge failed
    </span>
  ) : (
    <span className="animate-pulse whitespace-nowrap text-xs text-text-tertiary">Judging…</span>
  );
};

/** The review work-list for ONE candidate: a responsive grid of compact
 *  row-cards (a single stretched table can't use a wide monitor — the
 *  reason ends up a full screen away from its prompt). Grouped by
 *  template, items in queue order (reading order: left-to-right, then
 *  down) so the grid and the comparison page's Next button agree. */
const ReviewWorkList: FC<{
  rollup: MultiModelTestModelRollup;
  onOpenComparison: (comparisonId: number, promptKey?: string) => void;
  /** All of this model's cells are done — only then can an empty list
   *  mean "the judge passed everything". */
  settled: boolean;
}> = ({ rollup, onOpenComparison, settled }) => {
  // Ten templates make a LONG list — each group collapses as you work
  // through it. Keyed by comparison id, not template name: same-named
  // templates from different orgs are distinct groups. Resets when the
  // selected model changes (the caller keys this component by model).
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const toggleGroup = (comparisonId: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(comparisonId)) next.delete(comparisonId);
      else next.add(comparisonId);
      return next;
    });
  const items = rollup.review;
  if (items.length === 0) {
    if (settled) {
      return (
        <Paragraph displayType="normal" size="medium" className="mt-3 text-text-secondary">
          Nothing to review for this model — the judge passed every prompt.
        </Paragraph>
      );
    }
    // Still scoring — placeholder cards where flagged prompts will land.
    return (
      <div
        className="mt-4 grid animate-pulse grid-cols-1 gap-2 xl:grid-cols-2 min-[1800px]:grid-cols-3"
        aria-busy="true"
        aria-label="Scoring in progress"
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-11 rounded-md border border-border-subtle bg-bg-default" />
        ))}
      </div>
    );
  }
  const groups: { comparisonId: number; template: string; rows: MultiModelTestReviewItem[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.comparisonId === item.comparison_id) last.rows.push(item);
    else groups.push({ comparisonId: item.comparison_id, template: item.template_name, rows: [item] });
  }

  return (
    <div>
      {groups.map((group) => (
        <div key={group.comparisonId} className="mt-4">
          <button
            type="button"
            onClick={() => toggleGroup(group.comparisonId)}
            aria-expanded={!collapsed.has(group.comparisonId)}
            className="flex w-full items-center gap-2 rounded-md bg-bg-default px-2.5 py-1.5 text-left transition-colors hover:bg-bg-surface"
          >
            <span className="text-xs text-text-tertiary">{collapsed.has(group.comparisonId) ? "▸" : "▾"}</span>
            <span className="text-xs font-semibold uppercase tracking-wide text-text-primary">{group.template}</span>
            {collapsed.has(group.comparisonId) ? (
              <span className="text-xs text-text-tertiary">
                {group.rows.filter((r) => r.winner).length}/{group.rows.length} reviewed
              </span>
            ) : null}
          </button>
          {collapsed.has(group.comparisonId) ? null : (
            <div className="mt-2 grid grid-cols-1 gap-2 xl:grid-cols-2 min-[1800px]:grid-cols-3">
              {group.rows.map((item) => (
                <button
                  key={`${item.comparison_id}-${item.prompt_key}`}
                  type="button"
                  onClick={() => onOpenComparison(item.comparison_id, item.prompt_key)}
                  className={`group flex items-center justify-between gap-4 rounded-md border border-border-subtle bg-bg-surface px-3 py-2.5 text-left transition-colors hover:border-border-medium hover:bg-bg-default ${
                    item.winner ? "opacity-60" : ""
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    {item.winner ? (
                      <span className="shrink-0 font-semibold text-green-700">✓</span>
                    ) : item.seen ? (
                      // Seen but undecided — the reviewer opened it and
                      // backed out without a verdict.
                      <span className="shrink-0 text-text-secondary" title="Opened, no verdict yet" aria-label="Seen">
                        ◐
                      </span>
                    ) : (
                      <span className="h-2 w-2 shrink-0 rounded-full border border-border-medium" aria-hidden />
                    )}
                    <span className="min-w-0 truncate text-sm font-medium text-text-primary">
                      {item.field_title || item.prompt_key}
                    </span>
                    {item.winner ? <WinnerChip winner={item.winner} /> : null}
                  </span>
                  <span className="flex shrink-0 items-center gap-2.5">
                    {/* The verdict supersedes the flag reason — keeping a
                        red % after "candidate won" reads as still-failing. */}
                    {item.winner ? null : <ReasonBadge item={item} />}
                    <span className="text-text-tertiary opacity-0 transition-opacity group-hover:opacity-100">→</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

interface Props {
  testId: number;
  onBack: () => void;
  /** Open a comparison; promptKey scrolls its field card into view. */
  onOpenComparison: (comparisonId: number, promptKey?: string) => void;
}

/** The multiTest grid. Polling this screen IS the fan-out engine: each GET
 *  starts a bounded batch of pending runs and refreshes running ones
 *  server-side, so the multiTest advances as long as someone is watching. */
export const MultiModelTestScreen: FC<Props> = ({ testId, onBack, onOpenComparison }) => {
  // Selection lives in the URL, not state — navigating into a comparison
  // and back must not reset which model's work-list is open.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedModel = searchParams.get("model");
  const setSelectedModel = (model: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("model", model);
        return next;
      },
      { replace: true },
    );
  const {
    data: multiTest,
    isError,
    refetch,
  } = useQuery({
    queryKey: getGetEvalMultiModelTestQueryKey(testId),
    queryFn: async (): Promise<EvalMultiModelTestDetail | null> => {
      const resp = await getEvalMultiModelTest(testId);
      return resp.status === 200 ? resp.data : null;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && data.cells.some(cellInFlight) ? 2500 : false;
    },
  });

  // Confetti on the TRANSITION to fully reviewed — never on merely
  // arriving at an already-finished list. Lives above the early returns
  // (rules of hooks); the selection fallback mirrors reviewModel below.
  const [celebrate, setCelebrate] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const prevReviewRef = useRef<{ model: string; reviewed: number } | null>(null);
  useEffect(() => {
    if (!multiTest) return;
    const rollup =
      multiTest.models.find((m) => m.candidate_model === selectedModel) ??
      multiTest.models.find((m) => m.flagged > 0) ??
      multiTest.models[0];
    if (!rollup || rollup.review.length === 0) return;
    const reviewed = rollup.review.filter((i) => i.winner).length;
    const prev = prevReviewRef.current;
    if (
      prev &&
      prev.model === rollup.candidate_model &&
      prev.reviewed < rollup.review.length &&
      reviewed === rollup.review.length
    ) {
      setCelebrate(true);
    }
    prevReviewRef.current = { model: rollup.candidate_model, reviewed };
  }, [multiTest, selectedModel]);

  if (isError || multiTest === null) {
    return (
      <div className={MEASURE}>
        <Button text="← Model testing" appearance="secondary" className="rounded-sm" onClick={onBack} />
        <Paragraph displayType="normal" size="medium" className="mt-3 text-text-secondary">
          Couldn&apos;t load this test — the request may have timed out while runs were being advanced.
        </Paragraph>
        <Button text="Retry" appearance="primary" className="mt-2 rounded-sm" onClick={() => void refetch()} />
      </div>
    );
  }
  if (!multiTest) {
    // The first GET is also the first fan-out batch (it starts runs
    // server-side), so it can take several seconds.
    return (
      <div className="flex min-h-[60vh] items-center justify-center" aria-busy="true" aria-label="Loading test results">
        <ArrowPathIcon className="size-8 animate-spin text-text-tertiary" />
      </div>
    );
  }

  const templates = [...new Map(multiTest.cells.map((c) => [c.template_id, c.template_name])).entries()];
  // Per-comparison reviewed counts: matrix cells show their total score
  // once anything is reviewed, and flags count only what's LEFT to
  // review — verdicts live on review items, keyed by comparison.
  const reviewedByComparison = new Map<number, number>();
  for (const m of multiTest.models) {
    for (const item of m.review) {
      if (item.winner) {
        reviewedByComparison.set(item.comparison_id, (reviewedByComparison.get(item.comparison_id) ?? 0) + 1);
      }
    }
  }
  const cellFor = (templateId: number, model: string): MultiModelTestCell | undefined =>
    multiTest.cells.find((c) => c.template_id === templateId && c.candidate_model === model);
  const inFlight = multiTest.cells.filter(cellInFlight).length;
  const allSettled = inFlight === 0;
  // While anything is in flight, keep the cards in the test's declared
  // order — a ranking that reshuffles as partial averages land reads as
  // flakiness. Once settled, rank by total score (verdict-adjusted) so
  // cards climb as the reviewer's picks land; the judge's pass rate is
  // the pre-review tiebreak since adjusted == average until then.
  const displayModels = allSettled
    ? [...multiTest.models].sort(
        (a, b) => (b.adjusted_average ?? b.average ?? 0) - (a.adjusted_average ?? a.average ?? 0),
      )
    : [...multiTest.models].sort(
        (a, b) =>
          multiTest.candidate_models.indexOf(a.candidate_model) - multiTest.candidate_models.indexOf(b.candidate_model),
      );
  // Once EVERY model's flagged prompts are reviewed, crown the top-ranked
  // non-poor candidate. Requires at least one human verdict overall —
  // "recommended" is the reviewed conclusion, not an automatic guess.
  const fullyReviewed =
    allSettled &&
    multiTest.models.some((m) => m.flagged > 0) &&
    multiTest.models.every((m) => m.flagged === 0 || m.reviewed === m.flagged);
  const recommendedModel = fullyReviewed ? (displayModels.find((m) => !m.poor) ?? null) : null;
  // Fully reviewed but nobody qualifies: every candidate kept an
  // unresolved disqualification (the reviewer sided with the current
  // model on it) — say so instead of silently showing no crown.
  const noneRecommendable = fullyReviewed && recommendedModel === null;
  const reviewModel =
    displayModels.find((m) => m.candidate_model === selectedModel) ??
    displayModels.find((m) => m.flagged > 0) ??
    displayModels[0];
  const reviewItems = reviewModel?.review ?? [];
  const reviewedCount = reviewItems.filter((i) => i.winner).length;
  const nextUnreviewed = reviewItems.find((i) => !i.winner);

  const reviewModelSettled =
    reviewModel != null &&
    multiTest.cells.filter((c) => c.candidate_model === reviewModel.candidate_model).every((c) => !cellInFlight(c));

  return (
    <div className={`${MEASURE} flex flex-col gap-y-5`}>
      {celebrate ? <ConfettiBurst onDone={() => setCelebrate(false)} /> : null}
      <div>
        <Button text="← Model testing" appearance="secondary" className="rounded-sm" onClick={onBack} />
        <h2 className="mt-3 text-2xl font-semibold text-text-primary">{multiTest.name || `Test #${multiTest.id}`}</h2>
        <div className="mt-1 flex items-center gap-2">
          {inFlight > 0 ? (
            <span
              className="animate-pulse whitespace-nowrap rounded-full bg-rio-100 px-2 py-0.5 text-xs font-semibold text-rio-700"
              title={`${inFlight} of ${multiTest.cells.length} cells still running`}
            >
              In progress · {multiTest.cells.length - inFlight}/{multiTest.cells.length}
            </span>
          ) : (
            <span className="whitespace-nowrap rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
              Complete
            </span>
          )}
        </div>
      </div>

      <div>
        <div className="border-b border-border-medium pb-3">
          <h3 className="text-sm font-normal uppercase tracking-wide text-text-primary">Ranking</h3>
        </div>
        {/* The recommended legend hangs above its card — leave it headroom. */}
        <div className={`grid grid-cols-1 gap-3 lg:grid-cols-2 ${recommendedModel ? "mt-7" : "mt-2"}`}>
          {displayModels.map((rollup) => {
            const modelCells = multiTest.cells.filter((c) => c.candidate_model === rollup.candidate_model);
            const done = modelCells.filter((c) => !cellInFlight(c)).length;
            return (
              <ModelCard
                key={rollup.candidate_model}
                rollup={rollup}
                settled={done === modelCells.length}
                done={done}
                total={modelCells.length}
                winner={allSettled && displayModels[0]?.candidate_model === rollup.candidate_model}
                selected={reviewModel?.candidate_model === rollup.candidate_model}
                onSelect={() => setSelectedModel(rollup.candidate_model)}
                counts={sumScoreCounts(modelCells.map((c) => c.score_summary))}
                recommended={recommendedModel?.candidate_model === rollup.candidate_model}
              />
            );
          })}
        </div>
        {noneRecommendable ? (
          <p className="mt-2 text-sm text-text-secondary">
            No recommended model — every candidate has a disqualifying output the review upheld (picking the candidate
            or a tie on that prompt overrules the judge and clears it).
          </p>
        ) : null}
      </div>

      {reviewModel ? (
        <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
          <div className="-mx-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-border-medium px-4 pb-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <h3 className="text-sm font-normal uppercase tracking-wide text-text-primary">Review work-list</h3>
            </div>
            {reviewItems.length > 0 ? (
              <div className="flex items-center gap-3">
                {/* Redundant next to "All reviewed" — count only while working. */}
                {nextUnreviewed ? (
                  <span className="whitespace-nowrap text-sm font-medium text-text-secondary">
                    {reviewedCount} of {reviewItems.length} reviewed
                  </span>
                ) : null}
                {nextUnreviewed ? (
                  <Button
                    text={reviewedCount === 0 ? "Start reviewing →" : "Continue reviewing →"}
                    appearance="primary"
                    className="rounded-sm"
                    onClick={() => onOpenComparison(nextUnreviewed.comparison_id, nextUnreviewed.prompt_key)}
                  />
                ) : (
                  <span className="whitespace-nowrap text-sm font-medium text-green-700">All reviewed 🎉</span>
                )}
              </div>
            ) : null}
          </div>
          {/* Only once there IS progress — an all-track bar at 0 reviewed
              reads as a stray decorative band, and the "n of m reviewed"
              text already covers the zero state. */}
          {reviewItems.length > 0 && reviewedCount > 0 ? (
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-bg-default">
              <div
                className="h-full rounded-full bg-rio-500 transition-all"
                style={{ width: `${(reviewedCount / reviewItems.length) * 100}%` }}
              />
            </div>
          ) : null}
          <ReviewWorkList
            // Keyed by model so collapse state resets when the selection
            // changes instead of leaking across candidates.
            key={reviewModel.candidate_model}
            rollup={reviewModel}
            onOpenComparison={onOpenComparison}
            settled={reviewModelSettled}
          />
        </section>
      ) : null}

      {/* Collapsed by default — the cards carry the verdict; this is the
          per-template drill-down and the only way into cells with nothing
          flagged (spot-checks) or with failed runs. */}
      <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
        <button
          type="button"
          onClick={() => setMatrixOpen((v) => !v)}
          aria-expanded={matrixOpen}
          className={`-mx-4 flex w-full items-center gap-2 px-4 text-left ${matrixOpen ? "border-b border-border-medium pb-3" : ""}`}
        >
          <span className="text-xs text-text-tertiary">{matrixOpen ? "▾" : "▸"}</span>
          <h3 className="text-sm font-normal uppercase tracking-wide text-text-primary">Templates × models</h3>
          {!matrixOpen ? <span className="text-xs text-text-tertiary">per-template details</span> : null}
        </button>
        {matrixOpen ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-text-tertiary">
                  <th className="pb-2 pr-6 font-normal">Template</th>
                  {multiTest.candidate_models.map((model) => (
                    <th key={model} className="pb-2 pr-6 text-center font-normal" title={model}>
                      {modelLabel(model)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {templates.map(([templateId, templateName]) => {
                  // Every cell for a template shares one profile — read it
                  // off the first cell.
                  const profileName = multiTest.cells.find(
                    (c) => c.template_id === templateId,
                  )?.learner_test_profile_name;
                  // One model: the row is one comparison, so the whole row opens it.
                  const rowCell =
                    multiTest.candidate_models.length === 1
                      ? cellFor(templateId, multiTest.candidate_models[0])
                      : undefined;
                  return (
                    <tr
                      key={templateId}
                      onClick={rowCell ? () => onOpenComparison(rowCell.comparison_id) : undefined}
                      className={`group border-t border-border-subtle ${rowCell ? "cursor-pointer hover:bg-bg-default" : ""}`}
                    >
                      <td className="py-3 pr-6 align-middle">
                        <span className="font-medium text-text-primary">{templateName}</span>
                        <span className="mt-0.5 block text-xs text-text-tertiary">
                          profile: {profileName ?? "deleted"}
                        </span>
                      </td>
                      {multiTest.candidate_models.map((model) => {
                        const cell = cellFor(templateId, model);
                        if (!cell) {
                          return (
                            <td key={model} className="py-3 pr-6 text-xs text-text-tertiary">
                              —
                            </td>
                          );
                        }
                        const summary = cell.score_summary;
                        return (
                          <td key={model} className="py-2 pr-6 align-middle">
                            <button
                              type="button"
                              onClick={(event) => {
                                // The row may open the same comparison; open it once.
                                event.stopPropagation();
                                onOpenComparison(cell.comparison_id);
                              }}
                              className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-md border border-border-subtle bg-bg-default px-3 py-2 text-left transition-colors hover:border-border-medium hover:bg-bg-surface group-hover:border-border-medium"
                            >
                              {cellInFlight(cell) ? (
                                <span className="text-xs text-text-tertiary">
                                  {cell.progress.complete}/{cell.progress.total} runs
                                  {cell.progress.failed > 0 ? ` · ${cell.progress.failed} failed` : ""}
                                  {cell.progress.judging > 0 ? ` · ${cell.progress.judging} judging` : ""}
                                </span>
                              ) : summary.scored > 0 ? (
                                <span className="flex items-center gap-1.5">
                                  {reviewedByComparison.has(cell.comparison_id) ? (
                                    <span
                                      className="whitespace-nowrap rounded border border-border-medium px-1.5 py-0.5 text-xs font-semibold text-text-primary"
                                      title="Total score — this template's pass rate with your verdicts applied"
                                    >
                                      Total {Math.round((summary.adjusted_average ?? 0) * 100)}
                                    </span>
                                  ) : null}
                                  <span
                                    className={`rounded px-1.5 py-0.5 text-xs font-semibold ${scoreTone(summary)}`}
                                    title="Pass rate: prompts the judge called same or better"
                                  >
                                    {pct(summary.average)}
                                  </span>
                                  {/* Flags count what's LEFT to review, not what was
                                    ever flagged — a finished cell shows none. */}
                                  {summary.flagged - (reviewedByComparison.get(cell.comparison_id) ?? 0) > 0 ? (
                                    <span
                                      className="text-xs font-medium text-red-600"
                                      title="Flagged prompts not reviewed yet"
                                    >
                                      {summary.flagged - (reviewedByComparison.get(cell.comparison_id) ?? 0)}⚑
                                    </span>
                                  ) : null}
                                  {summary.unresolved_disqualified > 0 ? (
                                    <span
                                      className="whitespace-nowrap rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600"
                                      title={`${summary.unresolved_disqualified} disqualified output${summary.unresolved_disqualified === 1 ? "" : "s"} the review hasn't overruled`}
                                    >
                                      {summary.unresolved_disqualified}⚑ DQ
                                    </span>
                                  ) : null}
                                </span>
                              ) : (
                                <span className="text-xs text-text-tertiary">no scoreable slots</span>
                              )}
                              <span className="whitespace-nowrap text-xs text-text-tertiary">Open ›</span>
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};
